import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet } from "lucide-react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const KINDS = ["invoices", "payments", "journal"];

function startOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}
function endOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
}

function useToken() {
  return typeof window !== "undefined" ? localStorage.getItem("invoicepro.token") : null;
}

function FiltersForm({ kind, filters, setFilters }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:grid-cols-4">
      <div className="space-y-1">
        <Label className="text-xs">{t("exports.filters.from")}</Label>
        <Input
          type="date"
          value={filters.from || ""}
          onChange={(e) => setFilters({ ...filters, from: e.target.value })}
          data-testid={`exports-${kind}-from`}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("exports.filters.to")}</Label>
        <Input
          type="date"
          value={filters.to || ""}
          onChange={(e) => setFilters({ ...filters, to: e.target.value })}
          data-testid={`exports-${kind}-to`}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("exports.filters.format")}</Label>
        <Select
          value={filters.format || "xlsx"}
          onValueChange={(v) => setFilters({ ...filters, format: v })}
        >
          <SelectTrigger data-testid={`exports-${kind}-format`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="xlsx">XLSX</SelectItem>
            <SelectItem value="csv">CSV</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {kind === "invoices" && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">{t("exports.filters.status")}</Label>
            <Input
              value={filters.status || ""}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              placeholder="sent, paid, ..."
              data-testid={`exports-${kind}-status`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("exports.filters.currency")}</Label>
            <Input
              value={filters.currency || ""}
              onChange={(e) => setFilters({ ...filters, currency: e.target.value })}
              placeholder="SEK, USD, ..."
              data-testid={`exports-${kind}-currency`}
            />
          </div>
        </>
      )}
      {kind === "journal" && (
        <div className="space-y-1">
          <Label className="text-xs">{t("exports.filters.method")}</Label>
          <Select
            value={filters.method || "accrual"}
            onValueChange={(v) => setFilters({ ...filters, method: v })}
          >
            <SelectTrigger data-testid={`exports-${kind}-method`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="accrual">{t("exports.method.accrual")}</SelectItem>
              <SelectItem value="cash">{t("exports.method.cash")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1">
        <Label className="text-xs">{t("exports.filters.locale")}</Label>
        <Select
          value={filters.locale || "en"}
          onValueChange={(v) => setFilters({ ...filters, locale: v })}
        >
          <SelectTrigger data-testid={`exports-${kind}-locale`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">EN</SelectItem>
            <SelectItem value="sv">SV</SelectItem>
            <SelectItem value="ar">AR</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function ExportPanel({ kind }) {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    from: startOfMonth(),
    to: endOfMonth(),
    format: "xlsx",
    locale: "en",
    method: "accrual",
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const token = useToken();

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/exports/templates");
      setTemplates(data.filter((t) => t.kind === kind));
    } catch (err) {
      // silent
    }
  }, [kind]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const previewSource = useMemo(() => {
    if (kind === "invoices") return "/invoices";
    if (kind === "payments") return "/payments";
    return null;
  }, [kind]);

  const loadPreview = useCallback(async () => {
    if (!previewSource) {
      setPreview([]);
      return;
    }
    try {
      const params = new URLSearchParams();
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      params.set("page_size", "10");
      const { data } = await api.get(`${previewSource}?${params.toString()}`);
      setPreview(data.items || []);
    } catch (err) {
      setPreview([]);
    }
  }, [filters.from, filters.to, previewSource]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  async function download() {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.set(k, v);
      });
      const url = `${API_BASE}/exports/${kind}?${params.toString()}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : `${kind}.${filters.format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(t("exports.toast.downloaded"));
    } catch (err) {
      toast.error(formatApiError(err, t("exports.toast.failed")));
    } finally {
      setBusy(false);
    }
  }

  async function saveTemplate() {
    if (!templateName.trim()) return;
    try {
      await api.post("/exports/templates", {
        name: templateName.trim(),
        kind,
        filters,
        columns: [],
      });
      toast.success(t("exports.toast.templateSaved"));
      setTemplateName("");
      loadTemplates();
    } catch (err) {
      toast.error(formatApiError(err, t("exports.toast.templateFailed")));
    }
  }

  function applyTemplate(id) {
    const tmpl = templates.find((t) => t.id === id);
    if (tmpl) setFilters({ ...filters, ...tmpl.filters });
  }

  const previewCols =
    kind === "invoices"
      ? ["invoice_number", "customer.name", "issue_date", "total", "status"]
      : kind === "payments"
      ? ["invoice_number", "customer_name", "amount", "currency", "paid_at"]
      : [];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <FiltersForm kind={kind} filters={filters} setFilters={setFilters} />

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">{t("exports.templates.load")}</Label>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger data-testid={`exports-${kind}-templates`}>
                <SelectValue placeholder={t("exports.templates.pick")} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((tmpl) => (
                  <SelectItem key={tmpl.id} value={tmpl.id}>
                    {tmpl.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">{t("exports.templates.saveAs")}</Label>
            <div className="flex gap-2">
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder={t("exports.templates.namePlaceholder")}
                data-testid={`exports-${kind}-template-name`}
              />
              <Button
                type="button"
                variant="outline"
                onClick={saveTemplate}
                data-testid={`exports-${kind}-template-save`}
              >
                {t("common.save")}
              </Button>
            </div>
          </div>
          <Button
            onClick={download}
            disabled={busy}
            data-testid={`exports-${kind}-download`}
            className="bg-slate-900 text-white hover:bg-slate-800 gap-2"
          >
            {filters.format === "xlsx" ? (
              <FileSpreadsheet className="h-4 w-4" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {busy ? "..." : t("exports.download")}
          </Button>
        </div>
      </div>

      {previewCols.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
            {t("exports.preview.title")} ({preview.length})
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {previewCols.map((c) => (
                  <th key={c} className="px-4 py-2 text-start">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody data-testid={`exports-${kind}-preview`}>
              {preview.map((row, i) => (
                <tr key={i} className="border-t border-slate-100">
                  {previewCols.map((c) => {
                    const path = c.split(".");
                    let val = row;
                    for (const p of path) val = val?.[p];
                    return (
                      <td key={c} className="px-4 py-2">
                        {String(val ?? "")}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Exports() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6" data-testid="exports-page">
      <header className="space-y-1">
        <h1 className="font-heading text-3xl tracking-tight text-slate-900">
          {t("exports.title")}
        </h1>
        <p className="text-sm text-slate-500">{t("exports.subtitle")}</p>
      </header>

      <Tabs defaultValue="invoices" className="w-full">
        <TabsList data-testid="exports-tabs">
          {KINDS.map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`exports-tab-${k}`}>
              {t(`exports.tabs.${k}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map((k) => (
          <TabsContent key={k} value={k}>
            <ExportPanel kind={k} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
